import { ProductFeatureId } from '@/actions/permissionActions'
import { StripeProduct } from '@/actions/settings/billingActions'
import { ADDITIONAL_1040_COST } from '@/features/taxes/annual-taxes/constants'
import { StripeInterval } from '@/reducers/subscription.slice'
import { DateTime } from 'luxon'
import { ReactNode } from 'react'
import { UNLIMITED_STATE_FILINGS_COPY } from '@/features/user/signup/constants'
import type { PayrollPrices } from '@/features/payroll/payrollPrice.selectors'

export interface PlanDetail {
  text: string
  tooltip?: ReactNode
}

export interface PlanDetailMap {
  [key: string]: PlanDetail
}

const currentYear = DateTime.now().year
const filingYear = currentYear + 1

const bookkeepingDetails: PlanDetailMap = {
  monthlyBookkeepingServices: {
    text: 'Monthly done-for-you bookkeeping',
    tooltip:
      'Complete monthly bookkeeping service where our platform and your dedicated bookkeeping team keeps your books up-to-date, organized, and accurate.',
  },
  connectMultipleBankAccounts: {
    text: 'Connect multiple business bank accounts',
    tooltip:
      'Easily sync all your business checking, savings, and credit card accounts via a secure integration for complete financial tracking in one place.',
  },
  bookkeepingProfessionalsSpecialized: {
    text: 'Dedicated bookkeeping team',
    tooltip:
      'Your dedicated bookkeeping team reviews your transactions that are automatically categorized by the Heard platform to ensure accuracy and handles account reconciliation for your practice.',
  },
  taxReadyBooks: {
    text: 'Year-end tax ready books',
    tooltip:
      'Your books are organized and prepared for seamless annual tax filing within the Heard platform (must upgrade to essential or premium plan if Heard tax prep and filing is required or work with outside tax preparer).',
  },
  taxDeductionTracking: {
    text: 'Tax deduction tracking',
    tooltip:
      'We categorize and track all business expenses to ensure you capture every eligible tax deduction.',
  },
}

const cubkDetails: PlanDetailMap = {
  cubkReconciliation: {
    text: 'Complete reconciliation of past transactions',
    tooltip: 'Complete reconciliation of past transactions',
  },
  cubkProfessionalReview: {
    text: 'Professional review of deductible expenses',
    tooltip: 'Professional review of deductible expenses',
  },
  cubkFinancialInsights: {
    text: 'Year-to-date financial insights and reporting',
    tooltip: 'Year-to-date financial insights and reporting',
  },
}

const reportingDetails: PlanDetailMap = {
  monthlyFinancialReports: {
    text: 'Monthly income, expense, and profit reports and tracking',
    tooltip:
      "Get easy-to-understand financial reports showing your practice's performance, cash flow, and profitability trends over time.",
  },
  pnlStatements: {
    text: 'Profit and loss statements',
    tooltip:
      'Get professional P&L statements that clearly show your income, expenses, and net profit for tax purposes and business planning.',
  },
  allocationGuide: {
    text: 'Profit allocation guide',
    tooltip:
      'A planning tool that helps you determine how to distribute your profits across taxes, savings, retirement and more based on your financial situation.',
  },
  incomeAndGoalTracking: {
    text: 'Real-time income tracking and goal monitoring',
    tooltip:
      'Track your income in real-time and monitor progress toward an annual income goal throughout the year.',
  },
}

const supportDetails: PlanDetailMap = {
  assistedOnboarding: {
    text: 'Assisted onboarding with specialists',
    tooltip:
      'Group and one-on-one setup help from our team to get your account setup correctly from day one.',
  },
  unlimitedAppMessaging: {
    text: 'Unlimited messaging support for bookkeeping and general platform questions',
    tooltip:
      'Get help with day-to-day bookkeeping, platform features, and general business questions. Tax support not included, must upgrade to premium or essential plan.',
  },
  liveQnA: {
    text: 'Live support Q&A sessions',
    tooltip:
      'Group webinar sessions where you can ask questions and learn best practices for managing your Heard account.',
  },
}

const taxDetails: PlanDetailMap = {
  quarterlyTaxEstimates: {
    text: 'Quarterly tax estimates and payment guidance',
    tooltip:
      'Every quarter, we calculate your estimated state and federal tax payments, remind you when to pay so you never miss a deadline, and provide step-by-step guidance on how to pay.',
  },
  sCorpElectionGuidance: {
    text: 'S corporation election guidance and support',
    tooltip:
      "We'll assess if S corporation is right for your practice via a personalized readiness report. If S corporation makes sense, we’ll file Form 2553 for you to elect your therapy practice to be taxed as an S corporation by the IRS. We help you meet S corporation requirements: Incorporation and EIN changes.",
  },
  personalTaxPrep: {
    text: 'Personal income tax prep and filing',
    tooltip:
      "Our team files your annual state and federal personal income tax return (1040). Because we also do your bookkeeping, we're able to maximize your deductions which translates to more savings. If needed, we prepare and file your joint tax return with your spouse. We cannot file for the spouse if they are filing separately.",
  },
  businessTaxPrep: {
    text: 'Business income tax prep and filing',
    tooltip:
      "Our team files your annual state and federal business income tax return (Form 1120S). Because we also do your bookkeeping, we're able to maximize your deductions which translates to more savings.",
  },
  documentManagement: {
    text: 'Document management and automated filing reminders',
    tooltip:
      "Secure storage for all your tax documents with automatic reminders when it's time to file quarterly estimates and annual returns.",
  },
  freeExtensions: {
    text: 'Free tax extension filing support (as needed)',
    tooltip:
      "If you need more time to file your taxes come annual tax season, we'll handle filing extensions at no additional cost to keep you compliant with deadlines. We will establish a deadline for when you must file an extension request by.",
  },
  stateTaxFilingUnlimitedStates: {
    text: UNLIMITED_STATE_FILINGS_COPY.planDetail,
  },
  taxConsult: {
    text: 'Complimentary one-on-one tax consultation ($150 value)',
    tooltip:
      'Receive a 30 minute personalized tax consultation with your tax preparer to discuss your tax needs and strategy. You’ll be able to book additional 30 and 60 minute conversations during your tax filing process for a fee.',
  },
  taxConsultHalfOff: {
    text: '50% off one-on-one tax consultation ($75 value)',
    tooltip:
      'Receive 50% off a 30 minute personalized tax consultation with your tax preparer to discuss your tax needs and strategy. You’ll be able to book additional 30 and 60 minute conversations during your tax filing process for a fee.',
  },
}

const GUSTO_PAYROLL_INTRO =
  'We’ve teamed up with Gusto to provide stress-free payroll, embedded right in your Heard dashboard'

const valueNote = ({ monthly }: PayrollPrices) =>
  monthly ? ` (${monthly}/mo value)` : ''

const additionalEmployeesNote = ({ seat }: PayrollPrices) =>
  seat
    ? `Additional employees will cost ${seat}/mo per employee.`
    : 'Additional employees are billed monthly per employee.'

const payrollSetupDetail = (payrollPrices: PayrollPrices): PlanDetail => ({
  text: 'Payroll setup and management for yourself and employees',
  tooltip: `${GUSTO_PAYROLL_INTRO} so you can stay compliant as an S corporation and easily pay yourself and employees. Payroll for yourself is included if you are an S corporation${valueNote(payrollPrices)}. ${additionalEmployeesNote(payrollPrices)}`,
})

const payrollIncludedDetail = (payrollPrices: PayrollPrices): PlanDetail => ({
  text: 'Integrated payroll management via Gusto',
  tooltip: `${GUSTO_PAYROLL_INTRO}. Payroll for yourself is included${valueNote(payrollPrices)}. ${additionalEmployeesNote(payrollPrices)}`,
})

const payrollAddOnDetail = ({ base, seat }: PayrollPrices): PlanDetail => {
  const billing =
    base && seat
      ? `You’ll be billed ${base}/mo + ${seat}/mo per employee.`
      : 'You’ll be billed a monthly base fee + a monthly fee per employee.'
  return {
    text: 'Integrated payroll management via Gusto (Add on)',
    tooltip: `${GUSTO_PAYROLL_INTRO}. ${billing}`,
  }
}

// Stores the entire collection of "selling points" in a single object
const PlanDetails: PlanDetailMap = {
  annualBusinessTaxPrepSoloScorp: {
    text: `${currentYear} Annual business income tax prep and filing (filed in ${filingYear})`,
    tooltip:
      "Our team files your annual state and federal business income tax return. Because we also do your bookkeeping, we're able to maximize your deductions which translates to more savings.",
  },
  annualPersonalTaxPrepSCorp: {
    text: `${currentYear} Annual personal income tax prep and filing (filed in ${filingYear})`,
    tooltip: `Our team files your annual state and federal personal income tax return ($${ADDITIONAL_1040_COST} value). Because we also do your bookkeeping, we're able to maximize your deductions which translates to more savings.`,
  },
  annualIncomeTaxPrepSoleProp: {
    text: `${currentYear} Annual personal income tax prep and filing (filed in ${filingYear})`,
    tooltip:
      "Our team files your annual state and federal personal income tax return. Because we also do your bookkeeping, we're able to maximize your deductions which translates to more savings.",
  },
  quarterlyTaxEstimates: {
    text: 'Quarterly tax estimates',
    tooltip:
      'Every quarter, we calculate your estimated payments and remind you when to pay so you never miss a deadline.',
  },
  dedicatedTaxProfessional: {
    text: `Dedicated tax expert who supports you through your ${currentYear} tax filing`,
    tooltip:
      'Work directly with your dedicated tax expert during your tax filing to maximize your deductions.',
  },
  paidTaxAdvisory: {
    text: 'Personalized Tax Consultations (Add on)',
    tooltip:
      'You’ll be able to book 30 and 60 minute conversations with your tax preparer during your tax season for an additional fee.',
  },
  monthlyBookkeepingServices: {
    text: 'Monthly bookkeeping services',
    tooltip: null,
  },
  bookkeepingProfessionalsSpecialized: {
    text: 'Bookkeeping professionals specialized in therapy practice',
    tooltip:
      'Our only focus is therapists. That means your dedicated bookkeeping team and tax professionals are experts in therapy practices and all of the nuances that come with running one.',
  },
  unlimitedAppMessaging: {
    text: 'Unlimited in-app messaging support',
    tooltip:
      "You can message our support team through the Heard app 24/7 and we'll get back to you in 1-2 business days.",
  },
  financialReportsAndProfitAllocation: {
    text: 'Financial reports and profit allocation guide',
    tooltip:
      'We share monthly financial reports and allocation guide to help you confidently allocate your profit. Financial reports are automatically generated once bookkeeping is complete, with a summary of your cash flow and profit and loss. This helps you better understand your practice finances.',
  },
  expenseBreakdown: {
    text: 'Expense breakdown',
    tooltip: null,
  },
  singlePlaceToKeepDocuments: {
    text: 'A single place to keep important documents and receipts',
    tooltip: null,
  },
  sCorpCompliance: {
    text: 'S corporation compliance',
    tooltip:
      "We make sure you stay compliant and avoid fees or penalties from the IRS. We audit your books and advise you if you aren't compliant with S corporation tax regulations and provide resources for you to keep on hand so you understand how to remain in compliance.",
  },
  reasonableSalaryDetermination: {
    text: 'Reasonable salary determination',
    tooltip:
      "Throughout the year, we'll review your reasonable salary to make sure you're paying yourself the right amount. Setting a reasonable salary that is not too high or too low can affect your practice's profits and overall business success.",
  },
  sCorpElection: {
    text: 'S corporation election',
    tooltip:
      "We'll file Form 2553 for you to elect your therapy practice to be taxed as an S corporation by the IRS. We help you meet S corporation requirements: Incorporation and EIN changes. You can deduct certain business expenses, such as salaries, wages, bonuses, rent, utilities, and supplies. Deducting these expenses can reduce your taxable income and lower your taxes.",
  },
  sCorpEligibilityAssessment: {
    text: 'S corporation readiness report',
    tooltip:
      "We'll create a personalized readiness report to determine if it is a good time to elect S corporation, including an overview of the tax savings and associated costs.",
  },
  cubkReconciliation: {
    text: 'Complete reconciliation of past transactions',
    tooltip: 'Complete reconciliation of past transactions',
  },
  cubkProfessionalReview: {
    text: 'Professional review of deductible expenses',
    tooltip: 'Professional review of deductible expenses',
  },
  cubkFinancialInsights: {
    text: 'Year-to-date financial insights and reporting',
    tooltip: 'Year-to-date financial insights and reporting',
  },
}

// Construct arrays of plan details for each of the 4 sections - Tax, Bookkeeping, Budgeting & Tracking, S-Corporations

// Tax
export const getTaxSectionDetails = ({
  isScorp,
  permissions,
  planTierServices,
  isLitePlanEnabled,
  isPastCutoff,
  nextTaxYear,
}: {
  isScorp: boolean
  permissions: ProductFeatureId[]
  planTierServices: string[]
  isLitePlanEnabled: boolean
  isPastCutoff: boolean
  nextTaxYear: number
}) => {
  const details: PlanDetail[] = []
  if (isLitePlanEnabled) {
    if (permissions.includes(ProductFeatureId.quarterlyTaxes)) {
      details.push(taxDetails.quarterlyTaxEstimates)
    }
    if (planTierServices.includes(ProductFeatureId.sCorpEligibilitySupport)) {
      details.push(taxDetails.sCorpElectionGuidance)
    }
    if (permissions.includes(ProductFeatureId.annualTaxes)) {
      details.push(
        isPastCutoff
          ? {
              ...taxDetails.personalTaxPrep,
              text: `Personal income tax prep and filing for ${nextTaxYear} taxes`,
            }
          : taxDetails.personalTaxPrep
      )
      if (permissions.includes(ProductFeatureId.annualTaxesBusinessFiling)) {
        details.push(
          isPastCutoff
            ? {
                ...taxDetails.businessTaxPrep,
                text: `Business income tax prep and filing for ${nextTaxYear} taxes`,
              }
            : taxDetails.businessTaxPrep
        )
      }
      details.push(taxDetails.documentManagement)
      details.push(taxDetails.freeExtensions)
      details.push(taxDetails.stateTaxFilingUnlimitedStates)
    }
    return details
  }
  if (isScorp) {
    details.push(PlanDetails.annualBusinessTaxPrepSoloScorp)
    details.push(PlanDetails.annualPersonalTaxPrepSCorp)
  } else {
    details.push(PlanDetails.annualIncomeTaxPrepSoleProp)
  }
  details.push(
    PlanDetails.dedicatedTaxProfessional,
    PlanDetails.paidTaxAdvisory
  )

  return details
}

// Bookkeeping
export const getBookkeepingSectionDetails = ({
  isLitePlanEnabled,
}: {
  isLitePlanEnabled: boolean
}) => {
  if (isLitePlanEnabled) {
    return [
      bookkeepingDetails.monthlyBookkeepingServices,
      bookkeepingDetails.connectMultipleBankAccounts,
      bookkeepingDetails.bookkeepingProfessionalsSpecialized,
      bookkeepingDetails.taxReadyBooks,
      bookkeepingDetails.taxDeductionTracking,
    ]
  }
  return [
    PlanDetails.monthlyBookkeepingServices,
    PlanDetails.bookkeepingProfessionalsSpecialized,
    PlanDetails.unlimitedAppMessaging,
  ]
}

// Catch up bookkeeping
export const generalCUBKDetails = [
  cubkDetails.cubkReconciliation,
  cubkDetails.cubkProfessionalReview,
  cubkDetails.cubkFinancialInsights,
]

// When a payroll promo is active for the plan's tier, the caller passes the
// promo copy; otherwise the standard add-on line is shown. Tooltip is preserved
// either way (it still describes post-promo billing).
const payrollDetail = (
  payrollPrices: PayrollPrices,
  payrollPromoText?: string
): PlanDetail => {
  const addOn = payrollAddOnDetail(payrollPrices)
  return payrollPromoText
    ? { text: payrollPromoText, tooltip: addOn.tooltip }
    : addOn
}

// Budgeting & Tracking
export const getBudgetingSectionDetails = ({
  isGroup,
  isScorp,
  payrollPromoText,
  payrollPrices,
}: {
  isGroup: boolean
  isScorp: boolean
  payrollPromoText?: string
  payrollPrices: PayrollPrices
}) => {
  const details: PlanDetail[] = [
    PlanDetails.financialReportsAndProfitAllocation,
    PlanDetails.expenseBreakdown,
    PlanDetails.singlePlaceToKeepDocuments,
  ]
  if (!isScorp && isGroup) {
    details.push(payrollDetail(payrollPrices, payrollPromoText))
  }
  return details
}

export const getReportingSectionDetails = () => {
  return [
    reportingDetails.monthlyFinancialReports,
    reportingDetails.pnlStatements,
    reportingDetails.allocationGuide,
    reportingDetails.incomeAndGoalTracking,
  ]
}

// S Corporation
export const getSCorpSectionDetails = ({
  permissions,
  planTierServices,
  payrollPromoText,
  payrollPrices,
}: {
  permissions: ProductFeatureId[]
  planTierServices: string[]
  payrollPromoText?: string
  payrollPrices: PayrollPrices
}) => {
  const details: PlanDetail[] = []
  if (permissions.includes(ProductFeatureId.payrollIncludedForFreeForSelf)) {
    details.push(payrollIncludedDetail(payrollPrices))
  } else if (permissions.includes(ProductFeatureId.payroll)) {
    details.push(payrollDetail(payrollPrices, payrollPromoText))
  }
  if (planTierServices.includes(ProductFeatureId.sCorpComplianceSupport)) {
    details.push(PlanDetails.sCorpCompliance)
    details.push(PlanDetails.reasonableSalaryDetermination)
  }
  return details
}

export const getGroupPracticeSectionDetails = ({
  isLitePlanEnabled,
  planTierServices,
  payrollPrices,
}: {
  isLitePlanEnabled: boolean
  planTierServices: string[]
  payrollPrices: PayrollPrices
}) => {
  if (
    isLitePlanEnabled &&
    planTierServices.includes(ProductFeatureId.groupPracticeSupport)
  ) {
    return [payrollSetupDetail(payrollPrices)]
  }
  return []
}

export const getSupportSectionDetails = ({
  isLitePlanEnabled,
}: {
  isLitePlanEnabled: boolean
}) => {
  const details: PlanDetail[] = []
  if (isLitePlanEnabled) {
    details.push(
      supportDetails.assistedOnboarding,
      supportDetails.unlimitedAppMessaging,
      supportDetails.liveQnA
    )
  }
  return details
}

export const getProductByInterval = (
  products: StripeProduct[],
  interval: StripeInterval
) =>
  products.find(
    (product) => product.default_price.recurring.interval === interval
  )
